import { AlertTriangle, Briefcase, Calendar, Lock, MapPin, ShieldAlert, Truck, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { WORK_PERMIT_TYPES, formatDate, getActualStatus } from '../../work-permits/constants';
import { StoreStatusBadge } from './StoreStatusBadge';

export const STORE_WARNING = 'Bu talep bilginiz dahilinde değilse lütfen talebi iptal etmek için İSG birimi / Evrak Kontrol ile iletişime geçiniz.';

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div><p className="text-xs text-slate-500">{label}</p><div className="text-sm font-medium text-slate-800">{children}</div></div>
  </div>
);

const DefList = ({ title, items }) => items.length > 0 && (
  <div className="space-y-1">
    <p className="text-sm font-semibold text-slate-800">{title}</p>
    <ul className="space-y-1 pl-4 text-sm text-slate-700 list-disc">
      {items.map((d, i) => <li key={i}><b>{d.document}</b>{d.note ? `: ${d.note}` : ''}</li>)}
    </ul>
  </div>
);

const Deficiencies = ({ p }) => {
  const d = p.deficiencies || {};
  const any = p.deficiency_notes || d.company?.length || d.employees?.length || d.equipment?.length;
  if (!any) return null;
  return (
    <div className="space-y-3 rounded-xl border border-rose-200 bg-rose-50/60 p-4" data-testid="incoming-deficiencies">
      <p className="flex items-center gap-2 font-semibold text-rose-700"><AlertTriangle className="h-4 w-4" />Eksik / Hatalı Belgeler</p>
      {p.deficiency_notes && <p className="whitespace-pre-line text-sm text-slate-700">{p.deficiency_notes}</p>}
      <DefList title="Firma Belgeleri" items={d.company || []} />
      {(d.employees || []).map((e, i) => <DefList key={i} title={`${e.name}${e.position ? ` (${e.position})` : ''}`} items={e.items} />)}
      {(d.equipment || []).map((e, i) => <DefList key={i} title={`Ekipman: ${e.name}`} items={e.items} />)}
      <p className="flex items-center gap-1.5 text-xs text-slate-500"><Lock className="h-3 w-3" />Belgeler görüntülenemez; yalnızca eksiklik açıklamaları gösterilir.</p>
    </div>
  );
};

export const StorePermitDetail = ({ permit: p, onClose, onObject }) => (
  <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
    {p && (
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="incoming-permit-detail">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            <span className="rounded bg-slate-100 px-2 py-1 font-mono text-sm">{p.request_number || '-'}</span>
            <StoreStatusBadge permit={p} />
          </DialogTitle>
        </DialogHeader>
        {getActualStatus(p) === 'aktif' && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="incoming-detail-warning">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />{STORE_WARNING}
          </div>
        )}
        {p.rejection_reason && <p className="rounded-lg bg-rose-50 px-4 py-2 text-sm text-rose-700"><b>Red nedeni:</b> {p.rejection_reason}</p>}
        {p.withdrawal_reason && <p className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700"><b>Geri çekilme nedeni:</b> {p.withdrawal_reason}</p>}
        <div className="grid gap-4 rounded-xl border bg-slate-50/60 p-4 sm:grid-cols-2">
          <Row icon={Briefcase} label="Firma">{p.company_name}{p.on_behalf_of && <span className="block text-xs font-normal text-sky-700">{p.on_behalf_of} adına</span>}</Row>
          <Row icon={MapPin} label="Lokasyon / Mağaza">{p.location_name} · {p.store_name}</Row>
          <Row icon={Calendar} label="Tarih / Saat">{formatDate(p.start_date)} {p.start_time} — {formatDate(p.end_date)} {p.end_time}</Row>
          <Row icon={Briefcase} label="İş Tipi / İş Çeşidi">{p.is_tipi} / {p.is_cesidi}</Row>
          <div className="sm:col-span-2"><Row icon={Briefcase} label="Yapılacak İş">{p.job_description}</Row></div>
          {p.work_permit_types?.length > 0 && (
            <div className="flex flex-wrap gap-1 sm:col-span-2">
              {p.work_permit_types.map(t => <Badge key={t} variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{WORK_PERMIT_TYPES.find(x => x.id === t)?.label || t}</Badge>)}
            </div>
          )}
        </div>
        <Deficiencies p={p} />
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Users className="h-4 w-4" />Personel ({p.employees.length})</p>
          <div className="divide-y rounded-lg border">
            {p.employees.length === 0 ? <p className="p-3 text-sm text-slate-400">Personel eklenmemiş</p> : p.employees.map(e => (
              <div key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`incoming-employee-${e.id}`}>
                <span className="font-medium text-slate-800">{e.name}{e.task && <span className="font-normal text-slate-500"> – {e.task}</span>}</span>
                <span className="font-mono text-xs text-slate-500">{e.tc}</span>
              </div>
            ))}
          </div>
        </div>
        {p.equipment.length > 0 && <p className="flex items-center gap-2 text-sm text-slate-700"><Truck className="h-4 w-4 text-slate-400" />{p.equipment.join(', ')}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          {p.store_objection
            ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid="incoming-detail-objected">{p.store_objection.resolution ? `İSG kararı: ${p.store_objection.resolution.action === 'continue' ? 'talep devam ediyor' : 'talep iptal edildi'}${p.store_objection.resolution.note ? ` — ${p.store_objection.resolution.note}` : ''}` : 'İtirazınız iletildi'}</Badge>
            : <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onObject(p)} data-testid="incoming-detail-object-btn">Bilgim dahilinde değil</Button>}
          <Button variant="outline" onClick={onClose} data-testid="incoming-detail-close">Kapat</Button>
        </div>
      </DialogContent>
    )}
  </Dialog>
);
